/**
 * @file BinGenerateDialog.tsx
 * @description Генератор стеллажа: «стеллаж A: N полок × M ячеек»
 * (phase19.14, W14).
 *
 * Два шага: сначала `dryRun` (сервер считает, что создастся и что уже есть),
 * потом создание. Превью обязательно — стеллаж может дать до 9801 ячейки, и
 * создавать их вслепую нельзя.
 *
 * Поля и превью живут в `BinGenerateFields`: здесь остаются валидация,
 * два запроса и кнопки.
 */

import { useState } from "react";

import { BinGenerateFields } from "./BinGenerateFields";
import { Alert } from "../../ui/alert";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { useNotify } from "../../ui/NotifyProvider";
import {
  binsApi,
  MAX_BIN_SEGMENT,
  type GenerateBinsResultDto,
} from "../../../lib/api-bins";
import { wmsErrorText } from "../../../lib/wms-error-text";

interface Props {
  warehouseId: string;
  warehouseName: string;
  onCreated: () => void;
  onClose: () => void;
}

export function BinGenerateDialog({
  warehouseId,
  warehouseName,
  onCreated,
  onClose,
}: Props) {
  const notify = useNotify();
  const [rack, setRack] = useState("A");
  const [shelves, setShelves] = useState("3");
  const [cells, setCells] = useState("6");
  const [preview, setPreview] = useState<GenerateBinsResultDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function input() {
    return {
      warehouseId,
      rack: rack.trim().toUpperCase(),
      shelves: Number(shelves),
      cells: Number(cells),
    };
  }

  function validate(): boolean {
    const v = input();
    if (!/^[A-Z]$/.test(v.rack)) {
      setError("Стеллаж — одна буква A–Z.");
      return false;
    }
    if (
      !Number.isInteger(v.shelves) ||
      !Number.isInteger(v.cells) ||
      v.shelves < 1 ||
      v.cells < 1 ||
      v.shelves > MAX_BIN_SEGMENT ||
      v.cells > MAX_BIN_SEGMENT
    ) {
      setError(`Полок и ячеек — от 1 до ${MAX_BIN_SEGMENT}.`);
      return false;
    }
    return true;
  }

  async function runPreview() {
    setError(null);
    if (!validate()) return;
    setBusy(true);
    try {
      setPreview(await binsApi.generate({ ...input(), dryRun: true }));
    } catch (err) {
      setError(wmsErrorText(err, "Не удалось построить превью"));
    } finally {
      setBusy(false);
    }
  }

  async function create() {
    setError(null);
    setBusy(true);
    try {
      const result = await binsApi.generate(input());
      notify.success(
        `Создано ячеек: ${result.created.length}` +
          (result.skipped.length
            ? `, пропущено (уже есть): ${result.skipped.length}`
            : ""),
      );
      onCreated();
      onClose();
    } catch (err) {
      setError(wmsErrorText(err, "Не удалось создать ячейки"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Сгенерировать стеллаж</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 overflow-y-auto">
          <p className="text-sm text-on-surface-variant">Склад: {warehouseName}</p>
          <BinGenerateFields
            rack={rack}
            shelves={shelves}
            cells={cells}
            disabled={busy}
            preview={preview}
            onChange={(patch) => {
              // Параметры поменялись — старое превью недействительно.
              if (patch.rack !== undefined) setRack(patch.rack);
              if (patch.shelves !== undefined) setShelves(patch.shelves);
              if (patch.cells !== undefined) setCells(patch.cells);
              setPreview(null);
            }}
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" className="min-h-[56px]" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          {preview ? (
            <Button
              className="min-h-[56px]"
              onClick={() => void create()}
              disabled={busy || preview.created.length === 0}
            >
              {busy ? "Создаём…" : `Создать ${preview.created.length}`}
            </Button>
          ) : (
            <Button className="min-h-[56px]" onClick={() => void runPreview()} disabled={busy}>
              {busy ? "Считаем…" : "Показать превью"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
